/**
 * Servicio para la gestión de contratación de candidatos
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import type {
  Employee,
  HireEmployeeDto,
  ValidateCandidateResponse,
} from '@/types/employee.types';

// DTO para crear un empleado standalone (sin candidato/solicitud)
export interface CreateNewEmployeeDto extends HireEmployeeDto {
  firstName: string;
  lastName: string;
  email: string;
}

// Respuesta del endpoint de creación de empleado standalone
export interface CreateNewEmployeeResponse {
  employee: Employee;
  temporaryPassword: string;
}

// Tipos para JobRequest
interface JobRequest {
  id: number;
  areaId: number;
  areaVacante: string;
  puestoSolicitar: string;
  tipoPuesto?: string;
  sede: string;
  jornada: string;
  prioridad: string;
  numeroPlazas: number;
  fechaRequeridaInicio: string;
  habilidadesMinimas: string;
  salarioMinimo?: string;
  salarioMaximo?: string;
  status: string;
  solicitante?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  proyecto: { createdAt: string; description: string; id: string; name: string; updatedAt: string; } | undefined;

}

// Tipos para candidatos
interface Candidate {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  age: number;
  experienceYears: number;
  experienceDescription: string;
  cvUrl: string | null;
  privacyConsent: boolean;
  status: string;
  jobRequestId: number;
  userId: string;
  emailConfirmationToken: string | null;
  emailConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
  jobRequest?: JobRequest;
}

/**
 * Servicio de contratación con todos los endpoints disponibles
 */
export const hiringService = {
  /**
   * Obtener candidatos listos para contratar (estado SELECCIONADO)
   */
  getCandidatesReady: async (): Promise<Candidate[]> => {
    return apiRequest<Candidate[]>(`${API_URL}/hiring/candidates-ready`);
  },

  /**
   * Validar si un candidato puede ser contratado
   */
  validateCandidate: async (candidateId: string): Promise<ValidateCandidateResponse> => {
    return apiRequest<ValidateCandidateResponse>(
      `${API_URL}/hiring/validate-candidate/${candidateId}`
    );
  },

  /**
   * Obtener información de un candidato específico
   */
  getCandidateById: async (candidateId: string): Promise<Candidate> => {
    return apiRequest<Candidate>(`${API_URL}/candidates/profile/${candidateId}`);
  },

  /**
   * Contratar candidato (convertir a empleado)
   */
  hireCandidate: async (
    candidateId: string,
    data: HireEmployeeDto
  ): Promise<Employee> => {
    return apiRequest<Employee>(`${API_URL}/hiring/hire-candidate/${candidateId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Crear un empleado nuevo sin candidato/solicitud (alta standalone)
   */
  createNewEmployee: async (
    data: CreateNewEmployeeDto
  ): Promise<CreateNewEmployeeResponse> => {
    return apiRequest<CreateNewEmployeeResponse>(`${API_URL}/hiring/employees`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
